import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Formik, Form, useField } from 'formik'

import newsletterSchema from '~/schema/newsletter-schema'
import FullArrow from '~/svg/full-arrow.svg'

type FormState = 'preSubmit' | 'postSubmit' | 'success' | 'error'

const TextInput = ({
  label,
  filledAndValid,
  ...props
}: {
  [x: string]: any
  name: string
  filledAndValid: boolean
}) => {
  const [field, meta, helpers] = useField(props)

  return (
    <div className="w-full relative">
      {meta.touched && meta.error ? (
        <div className="error text-red-500 text-sm absolute -mt-8">
          {meta.error}
        </div>
      ) : null}
      <input
        required
        autoComplete="off"
        className="email-input text-grey-700 font-sans text-sm sm:text-base h-14 rounded-lg  bg-grey-600 bg-opacity-10 border-none w-full pl-4 ring-0 focus:ring-0 placeholder-current"
        {...field}
        {...props}
      />
      <style jsx>
        {`
          input:focus::placeholder {
            color: transparent;
          }
        `}
      </style>
    </div>
  )
}

const CheckboxInput = ({
  label,
  filledAndValid,
  ...props
}: {
  [x: string]: any
  name: string
  filledAndValid: boolean
}) => {
  const [field, meta, helpers] = useField(props)

  return (
    <div className="w-full relative">
      {meta.touched && meta.error ? (
        <div className="error text-red-500 text-sm absolute">*Required</div>
      ) : null}
      <div className="flex flex-row items-center mt-6">
        <input
          required
          type="checkbox"
          className="bg-transparent rounded-[0.1875rem] border-grey-700 mr-2 focus:ring-0 focus:outline-none focus:border-grey-700 text-grey-700"
          {...field}
        />
        <p className="text-grey-700 text-sm">
          By subscribing, you agree to our&nbsp;
          <Link href="/pages/privacy-policy">
            <a className="underline">Privacy Policy</a>
          </Link>
        </p>
      </div>
    </div>
  )
}

export default function NewsletterSignup({}) {
  const [newsletterState, setNewsletterState] = useState<FormState>('preSubmit')

  const newsletterSignup = async (data) => {
    setNewsletterState('postSubmit')

    const res = await fetch('/api/newsletter', {
      body: JSON.stringify({
        email: data.email,
        policy: data.policy
      }),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })
    if (res.status === 200) {
      setNewsletterState('success')
    } else if (res.status === 400) {
      setNewsletterState('error')
    }
  }
  return (
    <div className="">
      {newsletterState === 'preSubmit' || newsletterState === 'postSubmit' ? (
        <div className="flex flex-col py-16 md:py-20 justify-between h-full">
          <p className="text-grey-700 font-medium text-lg sm:text-xl pb-8">
            Join our mailing list for the
            <br />
            latest news and offers!
          </p>
          <Formik
            initialValues={{
              email: '',
              policy: false
            }}
            validationSchema={newsletterSchema}
            onSubmit={(values, actions) => {
              newsletterSignup(values)
              actions.setSubmitting(false)
            }}
          >
            {({ isValid, dirty }) => (
              <Form className="text-sm text-white font-light flex flex-col">
                <div className="justify-between flex flex-row items-center relative">
                  <TextInput
                    label="email"
                    name="email"
                    type="text"
                    placeholder="Email Address"
                    title="Email Address"
                    filledAndValid={isValid && dirty}
                  />
                  {!(isValid && dirty) || newsletterState === 'postSubmit' ? (
                    <button
                      className="bg-white text-black w-11 h-11 rounded absolute right-2 flex justify-center items-center"
                      type="submit"
                      disabled
                    >
                      <FullArrow />
                    </button>
                  ) : (
                    <button
                      className="bg-white text-black w-11 h-11 rounded absolute right-2 flex justify-center items-center"
                      type="submit"
                    >
                      <FullArrow />
                    </button>
                  )}
                </div>
                <CheckboxInput
                  label="policy"
                  name="policy"
                  type="policy"
                  title="Policy"
                  filledAndValid={isValid && dirty}
                />
              </Form>
            )}
          </Formik>
        </div>
      ) : newsletterState === 'success' ? (
        <div className="flex flex-col py-16 md:py-20  h-full">
          <p className="text-grey-700 font-medium text-lg sm:text-xl pb-8">
            Success!
          </p>
          <p className="text-grey-700 text-base ">You are now subscribed.</p>
        </div>
      ) : (
        <div className="flex flex-col py-16 md:py-20  h-full">
          <p className="text-grey-700 font-medium text-lg sm:text-xl pb-8">
            Whoops, looks like
            <br />
            something went wrong
          </p>
          <div
            className="bg-white rounded-full self-start py-6 px-8 cursor-pointer"
            onClick={() => {
              setNewsletterState('preSubmit')
            }}
          >
            <span className="text-grey-700 ">Try Again</span>
          </div>
        </div>
      )}
    </div>
  )
}
